import { useMutation } from "@apollo/client";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import { toFormat } from "@/lib/dateHelpers";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { DELETE_MEMBER_ELECTION_DATE_MUTATION, UPCOMING_MEMBER_ELECTION_DATES_QUERY } from "../queries";
import type {
  DeleteMemberElectionDateMutationResponse,
  DeleteMemberElectionDateMutationVariables,
  UpcomingMemberElectionDateNode
} from "../types";

export type DeleteElectionDateDialogProps = {
  open: boolean;
  electionDate: UpcomingMemberElectionDateNode | null;
  onClose(): void;
  onDeleted?(id: string): void;
};

export default function DeleteElectionDateDialog({
  open,
  electionDate,
  onClose,
  onDeleted
}: DeleteElectionDateDialogProps) {
  const [deleteMutation, { loading }] = useMutation<
    DeleteMemberElectionDateMutationResponse,
    DeleteMemberElectionDateMutationVariables
  >(DELETE_MEMBER_ELECTION_DATE_MUTATION, {
    refetchQueries: [{ query: UPCOMING_MEMBER_ELECTION_DATES_QUERY }]
  });

  const formattedDate = electionDate ? (toFormat(electionDate.electionDate, "MMM dd, yyyy") ?? "") : "";

  const handleConfirm = async () => {
    if (!electionDate) return;

    try {
      const { data } = await deleteMutation({ variables: { input: { id: electionDate.id } } });
      if (data?.deleteMemberElectionDate.success) {
        showSnackbar("Election date deleted");
        onDeleted?.(electionDate.id);
        onClose();
      } else {
        showErrorSnackbar(data?.deleteMemberElectionDate.message ?? "Unable to delete election date");
      }
    } catch (error) {
      showErrorSnackbar((error as Error).message);
    }
  };

  return (
    <Dialog
      open={open}
      headline="Delete Election Date?"
      onClosed={onClose}
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="delete-election-date-cancel">
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            onClick={handleConfirm}
            disabled={loading || !electionDate}
            testId="delete-election-date-confirm"
          >
            Delete
          </Button>
        </>
      }
    >
      <p className="text-body-medium">
        This will remove {formattedDate ? <strong>{formattedDate}</strong> : "this date"} from upcoming member
        elections. Eligible people will instead be elected on the next remaining date.
      </p>
    </Dialog>
  );
}
